import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { getCurrencyForCountry } from '../../utils/currencyUtils';
import '../../styles/filterAnimations.css';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Autocomplete,
  TextField,
  MenuItem,
  Chip,
  Button,
  CircularProgress,
  Alert,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Collapse,
  Fade,
  Slide,
  Badge,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  LocationOn as LocationOnIcon,
  Clear as ClearIcon,
  Search as SearchIcon,
  ExpandMore as ExpandMoreIcon,
  Category as CategoryIcon,
  AttachMoney as PriceIcon,
  Home as AmenitiesIcon,
  DateRange as DateIcon,
  TuneSharp as FilterIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useSearch } from '../../context/SearchContext';
import { useCountries, useCities } from '../../hooks/useFilterData';
import { useCategories } from '../../context/CategoryContext';
import DynamicCategoryFilter from './filters/DynamicCategoryFilter';
import PriceFilter from './filters/PriceFilter';
import AvailabilityFilter from './filters/AvailabilityFilter';
import ListingDetailsFilter from './ListingDetailsFilter';
import CountryFlag from '../common/CountryFlag';
import { PRODUCT_CONDITION_OPTIONS } from '../../constants/productConditions';

const getActiveDetailsEntries = (source = {}) =>
  Object.entries(source).reduce((acc, [key, value]) => {
    if (
      !key.startsWith('details_') ||
      value === undefined ||
      value === null ||
      value === '' ||
      (Array.isArray(value) && value.length === 0)
    ) {
      return acc;
    }

    acc[key] = Array.isArray(value) ? [...value] : value;
    return acc;
  }, {});

const getComparableFilterSnapshot = (draft) =>
  JSON.stringify({
    country: draft.country || '',
    cities: Array.isArray(draft.cities) ? [...draft.cities] : [],
    categoryIds: Array.isArray(draft.categoryIds) ? [...draft.categoryIds] : [],
    categoryId: draft.categoryId || null,
    condition: draft.condition || null,
    priceType: draft.priceType || null,
    minPrice: draft.minPrice ?? null,
    maxPrice: draft.maxPrice ?? null,
    currency: draft.currency || null,
    startDate: draft.startDate || null,
    endDate: draft.endDate || null,
    details: getActiveDetailsEntries(draft)
  });

// Créer les composants motion
const MotionDiv = motion.div;
const MotionSpan = motion.span;

/**
 * 🎯 FILTRES AVANCÉS - VERSION STRUCTURÉE ET ANIMÉE
 * 
 * Fonctionnalités:
 * ✅ Sections organisées et extensibles
 * ✅ Animations fluides et attrayantes
 * ✅ UI moderne et intuitive
 * ✅ Architecture prête pour nouveaux filtres
 */
// Collecte récursive dans une liste plate : les IDs de la catégorie elle-même + tous ses descendants
const collectDescendantIds = (categoryId, flatList) => {
  const ids = [categoryId];
  const directChildren = flatList.filter((c) => String(c.parentId) === String(categoryId));
  directChildren.forEach((child) => {
    ids.push(...collectDescendantIds(child.id, flatList));
  });
  return ids;
};

const AdvancedFiltersStructured = ({ onFiltersChange, onNotification, externalFilters, onFiltersApplied }) => {
  const { t } = useTranslation(['common', 'products']);
  const { updateFilters, filters, searchType } = useSearch();
  const { categoriesList } = useCategories();

  // 🎣 HOOKS POUR LES DONNÉES
  const {
    data: countries,
    loading: loadingCountries,
    error: countriesError,
    loadData: loadCountries
  } = useCountries();

  const {
    data: cities,
    loading: loadingCities,
    error: citiesError,
    loadData: loadCities,
    clearData: clearCities
  } = useCities();

  // 📍 ÉTATS LOCAUX
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [selectedCities, setSelectedCities] = useState([]);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [selectedCondition, setSelectedCondition] = useState(null);
  const [selectedPriceRange, setSelectedPriceRange] = useState(null);
  const [selectedAvailability, setSelectedAvailability] = useState(null);
  const [detailsFilters, setDetailsFilters] = useState({});
  const [isAnimating, setIsAnimating] = useState(false);
  const [hasUnappliedChanges, setHasUnappliedChanges] = useState(false);
  const [expandedSections, setExpandedSections] = useState({
    location: true,
    category: true,
    condition: false,
    price: false,
    amenities: false,
    availability: false,
    details: false
  });

  // 📍 REFS POUR LES COMPOSANTS
  const availabilityFilterRef = useRef(null);
 
  // 🌐 DEVISE DU PAYS SÉLECTIONNÉ (async pour supporter tous les pays)
  const [countryCurrency, setCountryCurrency] = useState(null);
  useEffect(() => {
    if (!selectedCountry?.code) {
      setCountryCurrency(null);
      return;
    }
    getCurrencyForCountry(selectedCountry.code)
      .then(info => setCountryCurrency(info?.code || null))
      .catch(() => setCountryCurrency(null));
  }, [selectedCountry?.code]);

  // 🎨 ANIMATIONS VARIANTS
  const sectionVariants = {
    hidden: { opacity: 0, y: 20, scale: 0.95 },
    visible: { 
      opacity: 1, 
      y: 0, 
      scale: 1,
      transition: { duration: 0.3, ease: "easeOut" }
    },
    exit: { 
      opacity: 0, 
      y: -20, 
      scale: 0.95,
      transition: { duration: 0.2 }
    }
  };

  const chipVariants = {
    hidden: { opacity: 0, scale: 0.8 },
    visible: { 
      opacity: 1, 
      scale: 1,
      transition: { duration: 0.2, ease: "easeOut" }
    }
  };

  const buttonVariants = {
    idle: { scale: 1 },
    hover: { scale: 1.05 },
    tap: { scale: 0.95 }
  };

  // 📊 COMPTER LES FILTRES ACTIFS - SIMPLIFIÉ
  const getActiveFiltersCount = useCallback(() => {
    let count = 0;
    if (selectedCountry) count++;
    if (selectedCities.length > 0) count++;
    if (selectedCategories.length > 0) count++;
    if (searchType !== 'listings' && selectedCondition) count++;
    if (selectedPriceRange?.minPrice || selectedPriceRange?.maxPrice) count++;
    if (selectedAvailability?._activeCount) count += selectedAvailability._activeCount;
    const activeDetailsFields = new Set();
    Object.entries(detailsFilters).forEach(([key, val]) => {
      if (val === undefined || val === null || val === '' || (Array.isArray(val) && val.length === 0)) return;
      const base = key.replace(/^details_/, '').replace(/_(min|max)$/, '');
      activeDetailsFields.add(base);
    });
    count += activeDetailsFields.size;
    return count;
  }, [searchType, selectedCountry, selectedCities, selectedCategories, selectedCondition, selectedPriceRange, selectedAvailability, detailsFilters]);

  const activeFiltersCount = getActiveFiltersCount();
  const hasFilters = activeFiltersCount > 0; // ✅ SIMPLE: vrai si des filtres existent
  const error = countriesError || citiesError;

  const buildCurrentDraftFilters = useCallback(() => {
    const selectedCategoryIds = selectedCategories.map((cat) => cat.id);

    // Étendre les IDs sélectionnés avec tous leurs descendants
    const expandedCategoryIds = [
      ...new Set(
        selectedCategoryIds.flatMap((id) =>
          collectDescendantIds(id, categoriesList)
        )
      ),
    ];

    return {
      country: selectedCountry?.code || '',
      cities: selectedCities.map((city) => (typeof city === 'string' ? city : city.name)),
      categoryIds: expandedCategoryIds,
      categoryId: selectedCategoryIds[0] || null,  // premier sélectionné (pas étendu) pour compatibilité
      condition: selectedCondition || null,
      priceType: selectedPriceRange?.priceType || null,
      minPrice: selectedPriceRange?.minPrice ?? null,
      maxPrice: selectedPriceRange?.maxPrice ?? null,
      currency: selectedPriceRange?.currency || null,
      startDate: selectedAvailability?.startDate || null,
      endDate: selectedAvailability?.endDate || null,
      ...getActiveDetailsEntries(detailsFilters)
    };
  }, [selectedCountry, selectedCities, selectedCategories, selectedCondition, selectedPriceRange, selectedAvailability, detailsFilters, categoriesList]);

  const appliedFilterSnapshot = useMemo(
    () => getComparableFilterSnapshot(filters),
    [filters]
  );

  const currentDraftSnapshot = useMemo(
    () => getComparableFilterSnapshot(buildCurrentDraftFilters()),
    [buildCurrentDraftFilters]
  );

  // 🔄 CHARGEMENT INITIAL
  useEffect(() => {
    loadCountries();
  }, []); // Suppression de loadCountries des dépendances

  // 🔍 RECHERCHE INITIALE - SIMPLIFIÉE
  useEffect(() => {
    if (countries.length > 0 && !filters._initialSearchDone) {
      const initialFilters = {
        ...filters,
        _initialSearchDone: true,
        _triggerSearch: true,
        _timestamp: Date.now()
      };
      
      updateFilters(initialFilters);
      onFiltersChange?.(initialFilters);
    }
  }, [countries.length, filters._initialSearchDone]); // Dépendances plus spécifiques

  // 🔄 SYNCHRONISATION SIMPLE
  useEffect(() => {
    if (filters.country && countries.length > 0 && !selectedCountry) {
      const country = countries.find(c => c.code === filters.country);
      if (country) {
        setSelectedCountry(country);
        loadCities({ country: country.code });
      }
    }
  }, [filters.country, countries.length, selectedCountry]); // Dépendances plus précises

  useEffect(() => {
    if (filters.condition && filters.condition !== selectedCondition) {
      setSelectedCondition(filters.condition);
    }

    if (!filters.condition && selectedCondition) {
      setSelectedCondition(null);
    }
  }, [filters.condition, selectedCondition]);

  const appliedDetailsRef = useRef(null);
  useEffect(() => {
    const nextDetails = getActiveDetailsEntries(filters);
    const nextDetailsStr = JSON.stringify(nextDetails);
    if (appliedDetailsRef.current !== nextDetailsStr) {
      appliedDetailsRef.current = nextDetailsStr;
      setDetailsFilters(nextDetails);
    }
  }, [filters]);

  // 🏷️ SYNCHRONISATION DES CATÉGORIES
  useEffect(() => {
    // Si on a des categoryIds dans les filtres mais pas de catégories sélectionnées
    if (filters.categoryIds && Array.isArray(filters.categoryIds) && filters.categoryIds.length > 0 && selectedCategories.length === 0) {
      // On pourrait charger les catégories depuis l'API, mais pour l'instant on laisse vide
      // TODO: Implémenter la synchronisation bidirectionnelle
      console.log('🏷️ CategoryIds dans les filtres:', filters.categoryIds);
    }
  }, [filters.categoryIds, selectedCategories.length]);

  // 🔄 GESTION DES FILTRES EXTERNES (recherches sauvegardées)
  useEffect(() => {
    if (externalFilters) {
      console.log('🔄 [AdvancedFilters] Applying external filters:', externalFilters);
      
      // Appliquer les filtres au contexte de recherche
      updateFilters(externalFilters);
      
      // Mettre à jour les états locaux
      if (externalFilters.country && countries.length > 0) {
        const country = countries.find(c => c.code === externalFilters.country);
        if (country && country !== selectedCountry) {
          setSelectedCountry(country);
          if (externalFilters.cities && externalFilters.cities.length > 0) {
            loadCities({ country: country.code });
          }
        }
      }
      
      if (externalFilters.cities && externalFilters.cities.length > 0) {
        setSelectedCities(externalFilters.cities);
      }
      
      if (externalFilters.categoryIds && externalFilters.categoryIds.length > 0) {
        // Pour les catégories, on va les laisser être gérées par DynamicCategoryFilter
        // en passant les IDs via le contexte de recherche
        console.log('🏷️ [AdvancedFilters] Categories will be handled by DynamicCategoryFilter:', externalFilters.categoryIds);
      }

      setSelectedCondition(externalFilters.condition || null);
      
      if (externalFilters.minPrice !== undefined || externalFilters.maxPrice !== undefined) {
        setSelectedPriceRange({
          minPrice: externalFilters.minPrice,
          maxPrice: externalFilters.maxPrice,
          priceType: externalFilters.priceType,
          currency: externalFilters.currency
        });
      }
      
      // Gestion des filtres de disponibilité
      if (externalFilters.startDate || externalFilters.endDate || externalFilters.immediateOnly !== undefined) {
        setSelectedAvailability({
          startDate: externalFilters.startDate,
          endDate: externalFilters.endDate,
          minDuration: externalFilters.minDuration,
          maxDuration: externalFilters.maxDuration,
          immediateOnly: externalFilters.immediateOnly,
          showUnavailable: externalFilters.showUnavailable,
          _activeCount: externalFilters.availabilityActiveCount || 0
        });
        console.log('📅 [AdvancedFilters] Availability filters applied from external:', {
          startDate: externalFilters.startDate,
          endDate: externalFilters.endDate,
          immediateOnly: externalFilters.immediateOnly
        });
      }

      setDetailsFilters(getActiveDetailsEntries(externalFilters));
      
      // Notifier que les filtres ont été appliqués
      onFiltersApplied?.();
    }
  }, [externalFilters, countries, selectedCountry, updateFilters, onFiltersApplied, loadCities]);

  useEffect(() => {
    setHasUnappliedChanges(currentDraftSnapshot !== appliedFilterSnapshot);
  }, [currentDraftSnapshot, appliedFilterSnapshot]);

  // 🎛️ GESTION DES SECTIONS
  const toggleSection = useCallback((section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  }, []);

  // 🔄 AUTO-APPLICATION DES FILTRES AVEC DEBOUNCING - DÉSACTIVÉ POUR RECHERCHE MANUELLE
  // useEffect(() => {
  //   if (hasUnappliedChanges) {
  //     const timeoutId = setTimeout(() => {
  //       // Construire les filtres avec toutes les données actuelles
  //       const searchFilters = {
  //         ...filters,
  //         country: selectedCountry?.code || '',
  //         cities: selectedCities.map(city => typeof city === 'string' ? city : city.name),
  //         // Filtres de catégories - CORRECTED: Envoyer toutes les catégories sélectionnées
  //         categoryIds: selectedCategories.map(cat => cat.id),
  //         // Filtres de prix
  //         priceType: selectedPriceRange?.priceType || null,
  //         minPrice: selectedPriceRange?.minPrice || null,
  //         maxPrice: selectedPriceRange?.maxPrice || null,
  //         currency: selectedPriceRange?.currency || null,
  //         _timestamp: Date.now(),
  //         _triggerSearch: true,
  //         _activeCount: getActiveFiltersCount()
  //       };

  //       updateFilters(searchFilters);
  //       onFiltersChange?.(searchFilters);
  //       setHasUnappliedChanges(false);
        
  //       // Animation du bouton
  //       setIsAnimating(true);
  //       setTimeout(() => setIsAnimating(false), 1000);
  //     }, 1200); // Délai augmenté à 1200ms pour laisser le temps de saisir

  //     return () => clearTimeout(timeoutId);
  //   }
  // }, [selectedCountry, selectedCities, selectedCategories, selectedPriceRange, hasUnappliedChanges, filters, updateFilters, onFiltersChange, getActiveFiltersCount]);

  // 🌍 GESTION DU CHANGEMENT DE PAYS - SIMPLIFIÉ
  const handleCountryChange = useCallback((event, newCountry) => {
    setSelectedCountry(newCountry);
    setSelectedCities([]);
    
    if (newCountry) {
      loadCities({ country: newCountry.code });
    } else {
      clearCities();
      // Immediately clear country in context so the sync effect doesn't re-apply it
      updateFilters({
        ...filters,
        country: '',
        cities: [],
        _timestamp: Date.now()
      });
    }
  }, [loadCities, clearCities, filters, updateFilters]);

  // 🏙️ GESTION DU CHANGEMENT DE VILLES - SIMPLIFIÉ
  const handleCitiesChange = useCallback((event, newCities) => {
    setSelectedCities(newCities);
  }, []);

  // 🏷️ GESTION DU CHANGEMENT DE CATÉGORIES - SIMPLIFIÉ
  const handleCategoriesChange = useCallback((newCategories) => {
    setSelectedCategories(newCategories);
  }, []);

  const handleConditionChange = useCallback((event) => {
    setSelectedCondition(event.target.value || null);
  }, []);

  // 💰 GESTION DU CHANGEMENT DE PRIX - SIMPLIFIÉ
  const handlePriceChange = useCallback((priceData) => {
    setSelectedPriceRange(priceData);
  }, []);

  // 📅 GESTION DU CHANGEMENT DE DISPONIBILITÉ - SIMPLIFIÉ
  const handleAvailabilityChange = useCallback((availabilityData) => {
    setSelectedAvailability(availabilityData);
  }, []);

  // 🔍 APPLIQUER LES FILTRES - VERSION ULTRA-SIMPLE
  const handleApplyFilters = useCallback(() => {
    const searchFilters = {
      ...filters,
      ...buildCurrentDraftFilters(),
      _timestamp: Date.now(),
      _triggerSearch: true,
      _activeCount: getActiveFiltersCount()
    };

    // Ajouter les filtres details_* pour la recherche JSONB
    // 1. Vider tous les details_* hérités du spread ...filters (mettre null pour écraser le reducer merge)
    Object.keys(searchFilters).forEach((k) => {
      if (k.startsWith('details_')) searchFilters[k] = null;
    });
    // 2. Re-injecter uniquement les valeurs actives (clés déjà préfixées)
    Object.entries(detailsFilters).forEach(([key, val]) => {
      if (val !== undefined && val !== null && val !== '' && !(Array.isArray(val) && val.length === 0)) {
        searchFilters[key] = val;
      }
    });

    updateFilters(searchFilters);
    onFiltersChange?.(searchFilters);
    onFiltersApplied?.(searchFilters);
  }, [filters, buildCurrentDraftFilters, detailsFilters, updateFilters, onFiltersChange, onFiltersApplied, getActiveFiltersCount]);

  // 🧹 RÉINITIALISER LES FILTRES - VERSION ULTRA-SIMPLE
  const handleClearFilters = useCallback(() => {
    setSelectedCountry(null);
    setSelectedCities([]);  
    setSelectedCategories([]);   
    setSelectedCondition(null);
    setSelectedPriceRange(null);
    setSelectedAvailability(null);
    setDetailsFilters({});
    clearCities();

    const clearedFilters = {
      ...filters,
      country: '',
      cities: [],
      categoryIds: [],
      categoryId: null,
      condition: null,
      priceType: null,
      minPrice: null,
      maxPrice: null,
      currency: null,
      startDate: null,
      endDate: null,
      _timestamp: Date.now(),
      _triggerSearch: true,
      _activeCount: 0
    };

    // Effacer les éventuels details_* qui seraient dans le contexte
    Object.keys(filters).forEach((k) => {
      if (k.startsWith('details_')) clearedFilters[k] = null;
    });

    updateFilters(clearedFilters);
    onFiltersChange?.(clearedFilters);
    onFiltersApplied?.(clearedFilters);
  }, [filters, updateFilters, onFiltersChange, clearCities, onFiltersApplied]);

  return (
    <Box sx={{ p: 0 }}> {/* Suppression du padding pour permettre aux sections d'occuper toute la largeur */}
      {/* 🚨 AFFICHAGE DES ERREURS */}
      <AnimatePresence>
        {error && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.3 }}
          >
            <Alert 
              severity="error" 
              sx={{ mb: 2, borderRadius: 2, mx: 3 }}
              onClose={() => {/* Les erreurs se nettoient automatiquement */}}
            >
              {error}
            </Alert>
          </motion.div>
        )}
      </AnimatePresence>

      {/* � COMPTEUR DE FILTRES ACTIFS */}
      <AnimatePresence>
        {activeFiltersCount > 0 && (
          <motion.div
            initial={{ opacity: 0, y: -10, scale: 0.9 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -10, scale: 0.9 }}
            transition={{ duration: 0.3 }}
          >
            <Box
              sx={{
                mx: 3,
                mb: 2,
                p: 2,
                borderRadius: 2,
                background: 'var(--filter-success-gradient)',
                color: '#2d5016',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontWeight: 'bold',
                boxShadow: 'var(--filter-shadow-light)',
                border: '2px solid rgba(45, 80, 22, 0.2)'
              }}
              className="filter-selected"
            >
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                🎯 {t('filters.activeFilters', '{{count}} filtre{{s}} actif{{s}}', { 
                  count: activeFiltersCount,
                  s: activeFiltersCount > 1 ? 's' : ''
                })}
              </Typography>
            </Box>
          </motion.div>
        )}
      </AnimatePresence>

      {/* 📅 SECTION DISPONIBILITÉ - DÉPLACÉE EN PREMIER */}
      <motion.div
        variants={sectionVariants}
        initial="hidden"
        animate="visible"
        transition={{ delay: 0.0 }}
        style={{ marginBottom: 16 }}
      >
        <Paper sx={{ borderRadius: 0, overflow: 'hidden', boxShadow: 'var(--filter-shadow-light)' }}>
          <AvailabilityFilter
            ref={availabilityFilterRef}
            onAvailabilityChange={handleAvailabilityChange}
            selectedAvailability={selectedAvailability}
            isExpanded={expandedSections.availability}
            onToggleExpand={() => toggleSection('availability')}
            onNotification={onNotification}
            externalFilters={externalFilters}
          />
        </Paper>
      </motion.div>

      {/* 📍 SECTION LOCALISATION - DÉPLACÉE EN SECOND */}
      <motion.div
        variants={sectionVariants}
        initial="hidden"
        animate="visible"
        transition={{ delay: 0.1 }}
        style={{ marginBottom: 16 }}
      >
        <Paper sx={{ borderRadius: 0, overflow: 'hidden', boxShadow: 'var(--filter-shadow-light)' }}>
          <Box
            sx={{
              p: 2,
              width: '100%',
              background: expandedSections.location 
                ? 'var(--filter-secondary-gradient)'
                : 'transparent',
              color: expandedSections.location ? 'white' : 'inherit',
              cursor: 'pointer',
              transition: 'var(--filter-transition-medium)',
              '&:hover': {
                background: expandedSections.location 
                  ? 'var(--filter-secondary-gradient)'
                  : 'rgba(79, 172, 254, 0.1)',
                transform: 'translateY(-1px)'
              }
            }}
            className={`filter-item-interactive ${expandedSections.location ? 'filter-selected' : ''}`}
            onClick={() => toggleSection('location')}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <LocationOnIcon sx={{ mr: 2 }} />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {t('filters.location.title', 'Localisation')}
                </Typography>
                <Badge 
                  badgeContent={selectedCountry ? (selectedCities.length > 0 ? selectedCities.length + 1 : 1) : 0} 
                  color="secondary"
                  sx={{ ml: 2 }}
                />
              </Box>
              
               
            </Box>
          </Box>

          <Collapse in={expandedSections.location} timeout={300}>
            <Box sx={{ p: 3, mx: 0 }}> {/* Padding interne seulement */}
              <Grid container spacing={3}>
                {/* 🌍 SÉLECTION DU PAYS */}
                <Grid item xs={12}>
                  <motion.div
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.4, delay: 0.1 }}
                  >
                    <Autocomplete
                      value={selectedCountry}
                      onChange={handleCountryChange}
                      options={countries}
                      getOptionLabel={(option) => `${option.name} (${option.count})`}
                      isOptionEqualToValue={(option, value) => option.code === value.code}
                      loading={loadingCountries}
                      disabled={loadingCountries}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label={t('filters.location.country', 'Pays')}
                          placeholder={t('filters.location.selectCountry', 'Sélectionnez un pays...')}
                          variant="outlined"
                          InputProps={{
                            ...params.InputProps,
                            endAdornment: (
                              <>
                                {loadingCountries && <CircularProgress color="inherit" size={20} />}
                                {params.InputProps.endAdornment}
                              </>
                            ),
                          }}
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              borderRadius: 2,
                              transition: 'all 0.3s ease',
                              '&:hover': {
                                boxShadow: '0 4px 12px rgba(79, 172, 254, 0.2)'
                              }
                            }
                          }}
                        />
                      )}
                      renderOption={(props, option) => (
                        <motion.li {...props} whileHover={{ backgroundColor: 'rgba(79, 172, 254, 0.1)' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                            <CountryFlag 
                              countryCode={option.code}
                              countryName={option.name}
                              size="medium"
                              style={{ marginRight: '8px' }}
                            />
                            <Typography variant="body2">
                              {option.name}
                            </Typography>
                            <Typography variant="caption" sx={{ ml: 'auto', color: 'text.secondary' }}>
                              {option.count} {t('listings', 'annonces')}
                            </Typography>
                          </Box>
                        </motion.li>
                      )}
                    />
                  </motion.div>
                </Grid>

                {/* 🏙️ SÉLECTION DES VILLES */}
                <AnimatePresence>
                  {selectedCountry && (
                    <Grid item xs={12}>
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.3 }}
                      >
                        <Autocomplete
                          multiple
                          value={selectedCities}
                          onChange={handleCitiesChange}
                          options={cities.map(city => city.name)}
                          loading={loadingCities}
                          disabled={loadingCities || !selectedCountry}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label={t('filters.location.cities', 'Villes')}
                              placeholder={t('filters.location.selectCities', 'Sélectionnez des villes en {{country}}', { country: selectedCountry.name })}
                              variant="outlined"
                              InputProps={{
                                ...params.InputProps,
                                endAdornment: (
                                  <>
                                    {loadingCities && <CircularProgress color="inherit" size={20} />}
                                    {params.InputProps.endAdornment}
                                  </>
                                ),
                              }}
                              sx={{
                                '& .MuiOutlinedInput-root': {
                                  borderRadius: 2,
                                  transition: 'all 0.3s ease',
                                  '&:hover': {
                                    boxShadow: '0 4px 12px rgba(79, 172, 254, 0.2)'
                                  }
                                }
                              }}
                            />
                          )}
                          renderTags={(value, getTagProps) =>
                            value.map((option, index) => (
                              <motion.div 
                                key={option}
                                variants={chipVariants}
                                initial="hidden"
                                animate="visible"
                                exit="hidden"
                              >
                                <Chip 
                                  variant="outlined" 
                                  label={option} 
                                  {...getTagProps({ index })}
                                  className="filter-chip-enter filter-item-interactive"
                                  sx={{ 
                                    borderRadius: 'var(--filter-border-radius)',
                                    background: 'var(--filter-success-gradient)',
                                    border: 'none',
                                    color: '#2d5016',
                                    fontWeight: 'bold',
                                    transition: 'var(--filter-transition-fast)',
                                    '&:hover': {
                                      transform: 'scale(1.05) translateY(-1px)',
                                      boxShadow: 'var(--filter-shadow-light)'
                                    }
                                  }}
                                />
                              </motion.div>
                            ))
                          }
                        />

                        {/* 📊 STATISTIQUES */}
                        {cities.length > 0 && (
                          <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ delay: 0.2 }}
                          >
                            <Typography variant="caption" sx={{ mt: 1, display: 'block', color: 'text.secondary' }}>
                              {selectedCities.length > 0 
                                ? t('filters.location.citiesSelected', '{{selected}} ville(s) sélectionnée(s) sur {{total}} disponibles', {
                                    selected: selectedCities.length,
                                    total: cities.length
                                  })
                                : t('filters.location.citiesAvailable', '{{count}} ville(s) disponible(s)', {
                                    count: cities.length
                                  })
                              }
                            </Typography>
                          </motion.div>
                        )}
                      </motion.div>
                    </Grid>
                  )}
                </AnimatePresence>
              </Grid>
            </Box>
          </Collapse>
        </Paper>
      </motion.div>

      {/* 🏷️ SECTION CATÉGORIES DYNAMIQUES */}
      <motion.div
        variants={sectionVariants}
        initial="hidden"
        animate="visible"
        transition={{ delay: 0.2 }}
        style={{ marginBottom: 16 }}
      >
        <Paper sx={{ borderRadius: 0, overflow: 'hidden', boxShadow: 'var(--filter-shadow-light)' }}>
          <DynamicCategoryFilter            
            selectedCategories={selectedCategories}
            onCategoryChange={handleCategoriesChange}
            onNotification={onNotification}
            isExpanded={expandedSections.category}
            onToggleExpand={() => toggleSection('category')}
            externalFilters={externalFilters}
          />
        </Paper>
      </motion.div>

      {/* 🔍 SECTION FILTRES DÉTAILS SPÉCIFIQUES */}
      {selectedCategories.length > 0 && selectedCategories[0]?.offer_type && (
        <motion.div
          variants={sectionVariants}
          initial="hidden"
          animate="visible"
          transition={{ delay: 0.22 }}
          style={{ marginBottom: 16 }}
        >
          <Paper sx={{ borderRadius: 0, overflow: 'hidden', boxShadow: 'var(--filter-shadow-light)' }}>
            <ListingDetailsFilter
              offerType={selectedCategories[0].offer_type}
              value={detailsFilters}
              onChange={setDetailsFilters}
              isExpanded={expandedSections.details}
              onToggleExpand={() => toggleSection('details')}
            />
          </Paper>
        </motion.div>
      )}

      {searchType !== 'listings' && (
        <motion.div
          variants={sectionVariants}
          initial="hidden"
          animate="visible"
          transition={{ delay: 0.25 }}
          style={{ marginBottom: 16 }}
        >
          <Paper sx={{ borderRadius: 0, overflow: 'hidden', boxShadow: 'var(--filter-shadow-light)' }}>
            <Box
              sx={{
                p: 2,
                width: '100%',
                background: expandedSections.condition
                  ? 'var(--filter-secondary-gradient)'
                  : 'transparent',
                color: expandedSections.condition ? 'white' : 'inherit',
                cursor: 'pointer',
                transition: 'var(--filter-transition-medium)',
                '&:hover': {
                  background: expandedSections.condition
                    ? 'var(--filter-secondary-gradient)'
                    : 'rgba(79, 172, 254, 0.1)',
                  transform: 'translateY(-1px)'
                }
              }}
              className={`filter-item-interactive ${expandedSections.condition ? 'filter-selected' : ''}`}
              onClick={() => toggleSection('condition')}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <CategoryIcon sx={{ mr: 2 }} />
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    {t('filters.condition', 'État')}
                  </Typography>
                  <Badge badgeContent={selectedCondition ? 1 : 0} color="secondary" sx={{ ml: 2 }} />
                </Box>
              </Box>
            </Box>

            <Collapse in={expandedSections.condition} timeout={300}>
              <Box sx={{ p: 3 }}>
                <TextField
                  select
                  fullWidth
                  label={t('filters.condition', 'État')}
                  value={selectedCondition || ''}
                  onChange={handleConditionChange}
                  variant="outlined"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        boxShadow: '0 4px 12px rgba(79, 172, 254, 0.2)'
                      }
                    }
                  }}
                >
                  <MenuItem value="">{t('filters.all', 'Tous')}</MenuItem>
                  {PRODUCT_CONDITION_OPTIONS.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {t(`products:${option.translationKey}`)}
                    </MenuItem>
                  ))}
                </TextField>
              </Box>
            </Collapse>
          </Paper>
        </motion.div>
      )}

      {/* 💰 SECTION PRIX */}
      <motion.div
        variants={sectionVariants}
        initial="hidden"
        animate="visible"
        transition={{ delay: 0.3 }}
        style={{ marginBottom: 16 }}
      >
        <Paper sx={{ borderRadius: 0, overflow: 'hidden', boxShadow: 'var(--filter-shadow-light)' }}>
          <PriceFilter
            onPriceChange={handlePriceChange}
            selectedPriceRange={selectedPriceRange}
            isExpanded={expandedSections.price}
            onToggleExpand={() => toggleSection('price')}
            onNotification={onNotification}
            externalFilters={externalFilters}
            disabled={!selectedCountry}
            forcedCurrency={countryCurrency}
          />
        </Paper>
      </motion.div>

      {/* ⚡ ACTIONS RAPIDES */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, delay: 0.3 }}
      >
        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 3, px: 3 }}> {/* Padding horizontal seulement pour les boutons */}
          <motion.div
            variants={buttonVariants}
            initial="idle"
            whileHover="hover"
            whileTap="tap"
          >
            <Button
              variant="outlined"
              startIcon={<ClearIcon />}
              onClick={handleClearFilters}
              className="filter-button-hover"
              sx={{
                borderRadius: 0,
                px: 2, // Réduit de 3 à 2
                py: 1, // Réduit de 1.5 à 1
                borderWidth: 2,
                fontSize: '0.875rem', // Réduction de la taille du texte
                transition: 'var(--filter-transition-medium)',
                '&:hover': {
                  borderWidth: 2,
                  boxShadow: 'var(--filter-shadow-medium)',
                  transform: 'translateY(-2px)'
                }
              }}
            >
              {t('filters.clear', 'Effacer')}
            </Button>
          </motion.div>
          
          <motion.div
            variants={buttonVariants}
            initial="idle"
            whileHover="hover"
            whileTap="tap"
          >
            <Button
              variant="contained"
              startIcon={<SearchIcon />}
              disabled={!hasUnappliedChanges}
              onClick={handleApplyFilters}
              sx={{
                borderRadius: 0,
                px: 3,
                py: 1,
                fontSize: '0.875rem',
                fontWeight: 600,
                background: hasUnappliedChanges ? '#6200ee' : '#9e9e9e',
                '&:hover': { background: hasUnappliedChanges ? '#4a00b4' : '#9e9e9e' },
                boxShadow: 'var(--filter-shadow-medium)',
                transition: 'var(--filter-transition-medium)',
                '&:hover': {
                  boxShadow: 'var(--filter-shadow-heavy)',
                  transform: 'translateY(-3px) scale(1.02)'
                },
                '&:disabled': {
                  opacity: 0.5,
                  transform: 'none'
                }
              }}
            >
              {hasUnappliedChanges ? t('filters.searchButton', 'Rechercher') : t('filters.noChanges', 'Aucun changement')}
            </Button>
          </motion.div>
        </Box>
      </motion.div>
    </Box>
  );
};

export default AdvancedFiltersStructured;
